import { c as _c } from "react/compiler-runtime";
import type { AgentGraphOutputs } from '@onefootprint/request-types/dashboard';
import { Card, Select } from '@onefootprint/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { artifactOptions, type OutputOption, verdictOptions } from '../store/outputs';
import { useBuilder } from '../store/provider';
import { isTaskNode } from '../store/types';

// Radix rejects an item whose value is the empty string, so "not set" travels as a sentinel.
const UNSET = '__unset__';
type OutputPickerProps = {
  id: string;
  label: string;
  value: string | undefined;
  unset: {
    label: string;
    description: string;
  };
  options: OutputOption[];
  onChange: (value: string | undefined) => void;
};
const OutputPicker = t0 => {
  const $ = _c(17);
  const {
    id,
    label,
    value,
    unset,
    options,
    onChange
  } = t0;
  const {
    isReadOnly
  } = useBuilder();
  let t1;
  if ($[0] !== unset) {
    t1 = {
      value: UNSET,
      ...unset
    };
    $[0] = unset;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== options || $[3] !== t1) {
    t2 = [t1, ...options];
    $[2] = options;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const items = t2;
  const t3 = value ?? UNSET;
  let t4;
  if ($[5] !== items) {
    t4 = current => items.find(item => item.value === current)?.label ?? current;
    $[5] = items;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onChange) {
    t5 = next => onChange(next === UNSET ? undefined : next);
    $[7] = onChange;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== id || $[10] !== isReadOnly || $[11] !== items || $[12] !== label || $[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <Select id={id} aria-label={label} className="w-64 max-sm:w-full" size="compact" disabled={isReadOnly} value={t3} options={items} customFindOptionLabel={t4} onChange={t5} />;
    $[9] = id;
    $[10] = isReadOnly;
    $[11] = items;
    $[12] = label;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
};
const SettingRow = t0 => {
  const $ = _c(10);
  const {
    title,
    hint,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-label-3 text-primary">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== hint) {
    t2 = <p className="mt-1 text-body-3 text-tertiary">{hint}</p>;
    $[2] = hint;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="min-w-0 flex-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== children || $[8] !== t3) {
    t4 = <Card.Body as="li" className="flex items-center gap-3 px-4 py-3 max-sm:flex-col max-sm:items-stretch">{t3}{children}</Card.Body>;
    $[7] = children;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
const EmptyHint = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className="shrink-0 text-body-3 text-quaternary">{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const FinalOutputs = () => {
  const $ = _c(64);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "agentBuilder.canvas.outputs"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t0);
  const {
    state,
    actions,
    isReadOnly
  } = useBuilder();
  const {
    bundle
  } = state;
  let t1;
  if ($[1] !== bundle.outputs) {
    t1 = bundle.outputs ?? {};
    $[1] = bundle.outputs;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const outputs = t1;
  let t2;
  if ($[3] !== actions || $[4] !== outputs) {
    t2 = patch => actions.setOutputs({
      ...outputs,
      ...patch
    });
    $[3] = actions;
    $[4] = outputs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const designate = t2;
  let t3;
  if ($[6] !== t) {
    t3 = option => option.description ?? t(_temp, {
      position: option.position
    });
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const stepName = t3;
  let t4;
  if ($[8] !== bundle || $[9] !== stepName) {
    let t5;
    if ($[11] !== stepName) {
      t5 = option_0 => ({
        ...option_0,
        description: stepName(option_0)
      });
      $[11] = stepName;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    t4 = verdictOptions(bundle).map(t5);
    $[8] = bundle;
    $[9] = stepName;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const verdicts = t4;
  let t5;
  if ($[13] !== bundle || $[14] !== stepName || $[15] !== t) {
    let t6;
    if ($[17] !== stepName || $[18] !== t) {
      t6 = option_1 => ({
        ...option_1,
        description: t(_temp2, {
          task: stepName(option_1)
        })
      });
      $[17] = stepName;
      $[18] = t;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    t5 = artifactOptions(bundle).map(t6);
    $[13] = bundle;
    $[14] = stepName;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const reports = t5;
  let reportHint;
  let t6;
  if ($[20] !== bundle.nodes || $[21] !== outputs.artifactKey || $[22] !== reports.length || $[23] !== t) {
    const starred = bundle.nodes.filter(isTaskNode).flatMap(_temp3).find(_temp4);
    let t7;
    bb0: {
      if (outputs.artifactKey) {
        let t8;
        if ($[26] !== t) {
          t8 = t(_temp5);
          $[26] = t;
          $[27] = t8;
        } else {
          t8 = $[27];
        }
        t7 = t8;
        break bb0;
      }
      if (starred) {
        t7 = t(_temp6, {
          name: starred.name || starred.id
        });
        break bb0;
      }
      let t8;
      if ($[28] !== reports.length || $[29] !== t) {
        t8 = reports.length > 0 ? t(_temp7) : t(_temp8);
        $[28] = reports.length;
        $[29] = t;
        $[30] = t8;
      } else {
        t8 = $[30];
      }
      t7 = t8;
    }
    reportHint = t7;
    t6 = starred ? t(_temp9, {
      name: starred.name || starred.id
    }) : t(_temp10);
    $[20] = bundle.nodes;
    $[21] = outputs.artifactKey;
    $[22] = reports.length;
    $[23] = t;
    $[24] = reportHint;
    $[25] = t6;
  } else {
    reportHint = $[24];
    t6 = $[25];
  }
  const reportUnsetHint = t6;
  let t7;
  if ($[31] !== t) {
    t7 = t(_temp11);
    $[31] = t;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t) {
    t8 = t(_temp12);
    $[33] = t;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t) {
    t9 = t(_temp13);
    $[35] = t;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== designate || $[38] !== outputs.judgmentKey || $[39] !== t || $[40] !== verdicts) {
    t10 = verdicts.length === 0 && !outputs.judgmentKey ? <EmptyHint>{t(_temp14)}</EmptyHint> : <OutputPicker id="agent-outputs-judgment" label={t(_temp15)} value={outputs.judgmentKey} unset={{
      label: t(_temp16),
      description: t(_temp17)
    }} options={verdicts} onChange={judgmentKey => designate({
      judgmentKey
    })} />;
    $[37] = designate;
    $[38] = outputs.judgmentKey;
    $[39] = t;
    $[40] = verdicts;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t8 || $[44] !== t9) {
    t11 = <SettingRow title={t8} hint={t9}>{t10}</SettingRow>;
    $[42] = t10;
    $[43] = t8;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t) {
    t12 = t(_temp18);
    $[46] = t;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== bundle.nodes || $[49] !== designate || $[50] !== isReadOnly || $[51] !== outputs.artifactKey || $[52] !== reportUnsetHint || $[53] !== reports || $[54] !== t) {
    t13 = reports.length === 0 && !outputs.artifactKey ? <EmptyHint>{bundle.nodes.some(isTaskNode) && !isReadOnly ? t(_temp19) : t(_temp20)}</EmptyHint> : <OutputPicker id="agent-outputs-report" label={t(_temp21)} value={outputs.artifactKey} unset={{
      label: t(_temp22),
      description: reportUnsetHint
    }} options={reports} onChange={artifactKey => designate({
      artifactKey
    })} />;
    $[48] = bundle.nodes;
    $[49] = designate;
    $[50] = isReadOnly;
    $[51] = outputs.artifactKey;
    $[52] = reportUnsetHint;
    $[53] = reports;
    $[54] = t;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== reportHint || $[57] !== t12 || $[58] !== t13) {
    t14 = <SettingRow title={t12} hint={reportHint}>{t13}</SettingRow>;
    $[56] = reportHint;
    $[57] = t12;
    $[58] = t13;
    $[59] = t14;
  } else {
    t14 = $[59];
  }
  let t15;
  if ($[60] !== t11 || $[61] !== t14 || $[62] !== t7) {
    t15 = <Card.Root><Card.List aria-label={t7}>{t11}{t14}</Card.List></Card.Root>;
    $[60] = t11;
    $[61] = t14;
    $[62] = t7;
    $[63] = t15;
  } else {
    t15 = $[63];
  }
  return t15;
};
export default FinalOutputs;
function _temp($) {
  return $.unnamedStep;
}
function _temp2($_0) {
  return $_0.reportWrittenBy;
}
function _temp3(task) {
  return task.artifacts;
}
function _temp4(artifact) {
  return artifact.isStarred;
}
function _temp5($_1) {
  return $_1.reportHint;
}
function _temp6($_2) {
  return $_2.reportFallbackStarred;
}
function _temp7($_3) {
  return $_3.reportFallbackLatest;
}
function _temp8($_4) {
  return $_4.reportFallbackNone;
}
function _temp9($_5) {
  return $_5.reportUnsetStarred;
}
function _temp10($_6) {
  return $_6.reportUnsetLatest;
}
function _temp11($_7) {
  return $_7.title;
}
function _temp12($_8) {
  return $_8.judgmentLabel;
}
function _temp13($_9) {
  return $_9.judgmentHint;
}
function _temp14($_10) {
  return $_10.judgmentEmpty;
}
function _temp15($_11) {
  return $_11.judgmentLabel;
}
function _temp16($_12) {
  return $_12.judgmentUnset;
}
function _temp17($_13) {
  return $_13.judgmentUnsetHint;
}
function _temp18($_14) {
  return $_14.reportLabel;
}
function _temp19($_15) {
  return $_15.reportStartBelow;
}
function _temp20($_16) {
  return $_16.reportEmpty;
}
function _temp21($_17) {
  return $_17.reportLabel;
}
function _temp22($_18) {
  return $_18.reportUnset;
}